import { Input } from '@flowus/common/components/input';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from 'src/common/components/icon';
import { useGlobalSearch } from 'src/hooks/block/use-global-search';
import debounce from 'lodash/debounce';
import { useCurrentSpace } from 'src/hooks/space/use-current-space';
import { useTocListOnSelect } from 'src/components/move-to/toc-list';
import { cache } from 'src/redux/store';
import { segmentsToText } from 'src/editor/utils/editor';
import { ReadonlyContext } from 'src/hooks/block/use-block-locked';
import { SearchItem } from 'src/components/search';
import { TocItem } from '../main/aside/toc/toc-item';
import { SelectPageContext } from './select-page-context';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';

// Make the interface more flexible to accommodate the actual data structure
interface SearchResultItem {
  uuid: string;
  pageId?: string;
  title?: string;
  text?: string;
  navs: {
    uuid: string;
    title?: string;
    type: any;
    [key: string]: any;
  }[];
  [key: string]: any;
}

interface SearchProps {
  selectedTeamPages?: Record<string, boolean>;
  selectedPersonPages?: Record<string, boolean>;
  selectedManualPages?: Record<string, any>;
}

export const Search: FC<SearchProps> = ({
  selectedTeamPages = {},
  selectedPersonPages = {},
  selectedManualPages = {},
}) => {
  const { t } = useAppTranslation();
  const isComposingRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const divRef = useRef<HTMLDivElement>(null);
  const [keyword, setKeyword] = useState('');
  const [results, setResults] = useState<SearchResultItem[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const globalSearch = useGlobalSearch();
  const currentSpace = useCurrentSpace();

  const patchKeyword = (value: string) => {
    setKeyword(value);
  };

  const performSearch = useCallback(
    async (searchText: string) => {
      if (!searchText.trim() || !currentSpace?.uuid) {
        setResults([]);
        setShowDropdown(false);
        return;
      }

      setIsLoading(true);
      setShowDropdown(true);
      try {
        const response = await globalSearch({
          space: currentSpace.uuid,
          query: searchText,
          source: 'quickFind',
          perPage: 10,
          page: 1,
          filters: {
            isTitleOnly: true,
          },
        });

        setResults(response.results as SearchResultItem[]);
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    },
    [globalSearch, currentSpace.uuid]
  );

  // Debounced search to prevent too many API calls
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      void performSearch(value);
    }, 800),
    [performSearch]
  );

  useEffect(() => {
    if (keyword) {
      void debouncedSearch(keyword);
    } else {
      setResults([]);
      setShowDropdown(false);
    }
  }, [keyword, debouncedSearch]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (divRef.current && !divRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div ref={divRef}>
      <div className="mt-3 mb-2.5 relative">
        <Input
          selection={[0, keyword.length]}
          onCompositionChange={(isComposing) => {
            isComposingRef.current = isComposing;
          }}
          inputRef={inputRef}
          autoFocus
          className="h-8 px-2.5"
          inputClassName="pl-1"
          placeholder={t('common.search')}
          value={keyword}
          onChange={(value) => patchKeyword(value)}
          addonBefore={<Icon size="middle" className="text-grey4" name="IcSearch" />}
          addonAfter={
            keyword && (
              <Icon
                size="middle"
                onClick={() => {
                  patchKeyword('');
                  inputRef.current?.focus();
                }}
                name={'IcUploadCancel'}
                className="text-grey4"
              />
            )
          }
        />

        {showDropdown && (
          <div className="absolute z-10 w-full bg-white shadow-lg rounded-md mt-1 max-h-80 overflow-y-auto overflow-x-hidden">
            {isLoading ? (
              <div className="flex justify-center items-center p-4">
                <div className="animate-spin h-5 w-5 border-2 border-gray-500 rounded-full border-t-transparent"></div>
              </div>
            ) : results.length > 0 ? (
              <ul>
                {results.map((item, index) => {
                  const isSelected =
                    selectedTeamPages[item.uuid] ||
                    selectedPersonPages[item.uuid] ||
                    selectedManualPages[item.uuid];

                  return (
                    <SelectPageContext.Provider value={true}>
                      <ReadonlyContext.Provider value={true}>
                        <TocItem key={index} uuid={item.uuid} deep={0} selected={isSelected} />
                      </ReadonlyContext.Provider>
                    </SelectPageContext.Provider>
                  );
                })}
              </ul>
            ) : (
              <div className="p-4 text-center text-gray-500">
                {keyword ? t('views.main.template_center.search_no_result') : ''}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
